import { Injectable, inject } from '@angular/core';
import { AlertController, ToastButton, ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
@Injectable({ providedIn: 'root' })
export class AppAlerts {
  toastController = inject(ToastController);
  alertsCtrl = inject(AlertController);
  translate = inject(TranslateService);

  async presentToast(position: 'top' | 'middle' | 'bottom') {
    const toast = await this.toastController.create({
      message: 'Hello World!',
      duration: 1500,
      position: position,
    });

    await toast.present();
  }

  async successSnackbar(
    message: string,
    opts?: {
      position?: 'top' | 'middle' | 'bottom';
      positionAnchor?: HTMLElement | string;
      buttons?: (ToastButton | string)[];
      duration?: number;
    },
  ) {
    const toast = await this.toastController.create({
      message: message,
      duration: opts?.duration ?? 1500,
      position: opts?.position ?? 'top',
      color: 'success',
      cssClass: 'mb-12',
      positionAnchor: opts?.positionAnchor,
      buttons: opts?.buttons,
    });

    await toast.present();
  }

  async successSnackbarTR(
    message: string,
    opts?: {
      position?: 'top' | 'middle' | 'bottom';
      positionAnchor?: HTMLElement | string;
      buttons?: (ToastButton | string)[];
      duration?: number;
    },
  ) {
    return this.successSnackbar(this.translate.instant(message), opts);
  }

  async dangerSnackbar(message: string, opts?: { position: 'top' | 'middle' | 'bottom'; duration?: number }) {
    const toast = await this.toastController.create({
      message: message,
      duration: opts?.duration ?? 1500,
      position: opts?.position ?? 'top',
      color: 'danger',
    });

    await toast.present();
  }

  async dangerSnackbarTR(message: string, opts?: { position: 'top' | 'middle' | 'bottom'; duration?: number }) {
    return this.dangerSnackbar(this.translate.instant(message), opts);
  }

  async warningSnackbar(message: string, opts?: { position: 'top' | 'middle' | 'bottom'; duration?: number }) {
    const toast = await this.toastController.create({
      message: message,
      duration: opts?.duration ?? 1500,
      position: opts?.position ?? 'top',
      color: 'warning',
    });

    await toast.present();
  }

  async warningSnackbarTR(message: string, opts?: { position: 'top' | 'middle' | 'bottom'; duration?: number }) {
    return this.warningSnackbar(this.translate.instant(message), opts);
  }

  async snackbarTR(
    message: string,
    opts?: {
      position?: 'top' | 'middle' | 'bottom';
      positionAnchor?: HTMLElement | string;
      buttons?: (ToastButton | string)[];
      duration?: number;
    },
  ) {
    const toast = await this.toastController.create({
      message: this.translate.instant(message),
      duration: opts?.duration ?? 1500,
      position: opts?.position ?? 'top',
      buttons: opts?.buttons,
    });
    await toast.present();
  }

  async yesNoAlert(input: {
    header: string;
    message: string;
    yesCaption?: string;
    noCaption?: string;
    role?: 'destructive' | 'cancel';
  }) {
    const effectiveRole = input.role ?? 'confirm';
    const alert = await this.alertsCtrl.create({
      header: input.header,
      message: input.message,
      cssClass: 'app-alert',
      buttons: [
        { text: input.yesCaption ?? 'Yes', role: effectiveRole },
        { text: input.noCaption ?? 'No', role: 'cancel' },
      ],
    });
    await alert.present();
    const { role } = await alert.onWillDismiss();
    return role === effectiveRole;
  }

  async yesNoAlertTR(input: {
    header: string;
    message: string;
    yesCaption?: string;
    noCaption?: string;
    role?: 'destructive' | 'cancel';
  }) {
    return this.yesNoAlert({
      header: this.translate.instant(input.header),
      message: this.translate.instant(input.message),
      yesCaption: this.translate.instant(input.yesCaption ?? 'COMMON.YES'),
      noCaption: this.translate.instant(input.noCaption ?? 'COMMON.NO'),
      role: input.role,
    });
  }

  async confirmAlert(input: {
    header: string;
    message: string;
    yesCaption?: string;
    noCaption?: string;
    role?: 'destructive' | 'cancel';
  }) {
    const effectiveRole = input.role ?? 'confirm';
    const alert = await this.alertsCtrl.create({
      header: input.header,
      message: input.message,
      cssClass: 'app-alert',
      backdropDismiss: false,
      buttons: [{ text: input.yesCaption ?? 'Yes', role: effectiveRole }],
    });
    await alert.present();
    const { role } = await alert.onWillDismiss();
    return role === effectiveRole;
  }

  async confirmAlertTR(input: {
    header: string;
    message: string;
    yesCaption?: string;
    role?: 'destructive' | 'cancel';
  }) {
    return this.confirmAlert({
      header: this.translate.instant(input.header),
      message: this.translate.instant(input.message),
      yesCaption: this.translate.instant(input.yesCaption ?? 'COMMON.YES'),
      role: input.role,
    });
  }
}
